import { fromUnixTime, addMonths, isBefore } from 'date-fns';
import formatNumber from './format-number';
import { sleep } from './common';
import { updateCustomerDiscount, fetchCouponDetails, getDiscountDetails } from '../data/api';

/**
 * @typedef {object} StripeDiscount
 * @property {import('stripe').Stripe.Coupon} coupon
 */

/**
 * @typedef {{[key: string]: import('stripe').Stripe.Coupon}} CouponOverwritesConfig
 */

const INITIAL_RETRY_DELAY_MS = 2000; // Initial delay for retries
const BACKOFF_MULTIPLIER = 1.5; // Multiplier for exponential backoff

/**
 * Gets signup coupon IDs from environment variables
 * @returns {{signupCoupon: string|undefined, signupCouponAnnual: string|undefined}}
 */
function getSignupCouponIds() {
    return {
        signupCoupon: process.env.REACT_APP_SIGNUP_COUPON,
        signupCouponAnnual: process.env.REACT_APP_SIGNUP_COUPON_ANNUAL,
    };
}

/**
 * Gets coupon overwrites configuration from environment variables.
 * The annual equivalent of the LAUNCH coupon has a different discount
 * percentage in Stripe than the monthly one, but we want to show the
 * same discount. This is general behavior of annual vs. monthly coupons,
 * as only the first payment is discounted for annual plans, not the
 * monthly equivalent.
 * @returns {CouponOverwritesConfig}
 */
function getCouponOverwrites() {
    const { signupCoupon, signupCouponAnnual } = getSignupCouponIds();

    if (!signupCoupon || !signupCouponAnnual) {
        return {};
    }

    return {
        [signupCouponAnnual]: {
            id: signupCoupon,
            percent_off: 50,
            duration: 'repeating',
            duration_in_months: 3,
            amount_off: null,
            currency: 'usd',
            max_redemptions: null,
            redeem_by: null,
            metadata: {},
        },
    };
}

/**
 *
 * @param {{discount: StripeDiscount, config: CouponOverwritesConfig}} props
 * @returns {StripeDiscount | null} The processed discount object or null.
 */
export function applyCouponOverwrites(discount, config = null) {
    if (!discount || !discount.coupon || !discount.coupon.id) {
        return discount;
    }

    // Use provided config or get from environment variables
    const couponConfig = config || getCouponOverwrites();

    // Apply coupon overwrites if needed
    let processedDiscount = discount;

    if (couponConfig[discount.coupon.id]) {
        const coupon = { ...discount.coupon, ...couponConfig[discount.coupon.id] };
        processedDiscount = { ...discount, coupon };
    }

    return processedDiscount;
}

/**
 * Ensures the correct coupon is applied based on the selected billing period
 * Will update the coupon in Stripe on the customer if necessary, but only if
 * the SIGNUP_COUPON and SIGNUP_COUPON_ANNUAL environment variables are configured.
 * @param {object} params
 * @param {'month'|'year'} params.selectedPeriod - The billing period selected
 * @param {StripeDiscount} params.discount - The current discount object with coupon
 * @param {string} params.customerId - The Stripe customer ID
 * @returns {Promise<StripeDiscount>} Updated discount object or original discount if no change needed
 */
export async function ensureCorrectSignupCouponForPeriod({ selectedPeriod, discount, customerId }) {
    if (discount === null || discount === undefined || !discount.coupon?.id || !customerId) {
        return discount;
    }

    const { signupCoupon, signupCouponAnnual } = getSignupCouponIds();

    // Only apply special handling if environment variables are configured
    if (!signupCoupon || !signupCouponAnnual) {
        return discount;
    }

    const couponId = discount.coupon.id;

    // Check if we need to switch coupons
    if (selectedPeriod === 'year' && couponId === signupCoupon) {
        // Switch from monthly to annual coupon
        return await _updateAndVerifySignupCoupon({
            customerId,
            targetCouponId: signupCouponAnnual,
            errorMessage: `Failed to apply annual signup coupon ${signupCouponAnnual}`,
        });
    } else if (selectedPeriod === 'month' && couponId === signupCouponAnnual) {
        // Switch from annual to monthly coupon
        return await _updateAndVerifySignupCoupon({
            customerId,
            targetCouponId: signupCoupon,
            errorMessage: `Failed to apply monthly signup coupon ${signupCoupon}`,
        });
    }

    // No change needed - return original discount
    return discount;
}

/**
 * @typedef {object} CouponDiscountData
 * @property {import('stripe').Stripe.Coupon} coupon required
 * @property {number|null} currentTotal optional, required if calculateTotal is true
 * @property {boolean} calculateTotal optional, default true. If false, returns the discount value as a percentage or fixed amount
 * @property {boolean} format optional, default false. If true, returns the discount value as a formatted string
 */
/**
 * @param {CouponDiscountData} data
 * @returns {number|string} The calculated discount value, either as a number or formatted string
 */
export function getCouponDiscount({
    coupon,
    currentTotal = null,
    calculateTotal = true,
    format = false,
}) {
    let discount = 0;
    let symbol = '$';

    if (!coupon) {
        return discount;
    }

    // We need at least one of these to calculate the coupon discount
    if (!coupon?.percent_off && !coupon?.amount_off) {
        return discount;
    }

    // For a percentage based coupon, we need a total to calculate the discount
    if (
        calculateTotal &&
        (!currentTotal || typeof currentTotal !== 'number') &&
        coupon?.percent_off
    ) {
        return discount;
    }

    if (coupon?.percent_off) {
        // When we're calculating the total, we won't return the percentage value
        // as a percentage, but as a fixed amount and therefore need to change the symbol
        // to a currency symbol
        if (!calculateTotal) {
            symbol = '%';
        }
        discount = calculateTotal ? (currentTotal * coupon.percent_off) / 100 : coupon.percent_off;
    } else {
        discount = coupon?.amount_off / 100;
    }

    return format ? formatNumber(discount, { symbol }) : discount;
}

/**
 * Determines which coupon should be active based on the selected period
 * and the current discount state
 * @param {string} selectedPeriod - 'month' or 'year'
 * @param {StripeDiscount} currentDiscount - Current discount object with coupon
 * @returns {{couponId: string|null, needsUpdate: boolean}}
 */
export function determineSignupCouponForPeriod(selectedPeriod, currentDiscount) {
    if (!currentDiscount?.coupon?.id) {
        return { couponId: null, needsUpdate: false };
    }

    // Don't attempt to switch coupons if the current coupon is invalid
    if (currentDiscount?.coupon?.valid === false) {
        return { couponId: null, needsUpdate: false };
    }

    const { signupCoupon, signupCouponAnnual } = getSignupCouponIds();

    // Only apply special handling if environment variables are present
    // If environment variables are not configured, don't apply special handling
    if (!signupCoupon || !signupCouponAnnual) {
        return { couponId: currentDiscount.coupon.id, needsUpdate: false };
    }

    const currentCouponId = currentDiscount.coupon.id;

    // Check if we need to switch coupons
    if (selectedPeriod === 'year' && currentCouponId === signupCoupon) {
        return { couponId: signupCouponAnnual, needsUpdate: true };
    } else if (selectedPeriod === 'month' && currentCouponId === signupCouponAnnual) {
        return { couponId: signupCoupon, needsUpdate: true };
    }

    // No change needed
    return { couponId: currentCouponId, needsUpdate: false };
}

/**
 * Determines which coupons need to be fetched from Stripe based on current
 * discount and environment variables
 * @param {StripeDiscount} discount - Current discount object
 * @returns {{fetchedCoupons: {[key: string]: import('stripe').Stripe.Coupon}, couponToFetch: string|null}} Object with current coupon data and ID of coupon to fetch
 */
export function determineSignupCouponsToFetch(discount) {
    const fetchedCoupons = {};
    let couponToFetch = null;

    if (!discount?.coupon?.id) {
        return { fetchedCoupons, couponToFetch };
    }

    const { signupCoupon, signupCouponAnnual } = getSignupCouponIds();

    // Check if we need to fetch the alternate coupon data
    if (!signupCoupon || !signupCouponAnnual) {
        return { fetchedCoupons, couponToFetch };
    }

    // Always store the current coupon data
    fetchedCoupons[discount.coupon.id] = discount.coupon;

    // Determine which coupon we need to fetch
    if (discount.coupon.id === signupCoupon) {
        couponToFetch = signupCouponAnnual;
    } else if (discount.coupon.id === signupCouponAnnual) {
        couponToFetch = signupCoupon;
    }

    return { fetchedCoupons, couponToFetch };
}

/**
 * Fetches the alternate coupon data if needed
 * @param {string} couponToFetch - ID of coupon to fetch
 * @param {{[key: string]: import('stripe').Stripe.Coupon}} fetchedCoupons - Already fetched coupons
 * @returns {Promise<{fetchedCoupons: {[key: string]: import('stripe').Stripe.Coupon}, error: Error|null}>} Result object with fetchedCoupons and error
 */
export async function fetchAlternateSignupCoupon(couponToFetch, fetchedCoupons) {
    if (!couponToFetch || fetchedCoupons[couponToFetch]) {
        return { fetchedCoupons, error: null };
    }

    try {
        const result = await fetchCouponDetails({ couponId: couponToFetch });

        // Check if the API returned an error
        if (result?.error) {
            return {
                fetchedCoupons,
                error: result.error,
            };
        }

        if (result?.coupon) {
            return {
                fetchedCoupons: {
                    ...fetchedCoupons,
                    [couponToFetch]: result.coupon,
                },
                error: null,
            };
        }
    } catch (error) {
        return {
            fetchedCoupons,
            error,
        };
    }

    // If we get here, something unexpected happened
    return {
        fetchedCoupons,
        error: new Error('Unexpected response from coupon API'),
    };
}

/**
 * Creates an expected discount object based on the current discount and target period
 * @param {StripeDiscount} currentDiscount - Current discount object
 * @param {string} targetCouponId - Target coupon ID
 * @param {{[key: string]: import('stripe').Stripe.Coupon}} fetchedCoupons - Object containing fetched coupon data by ID
 * @returns {StripeDiscount|null} Expected discount object
 */
export function createExpectedDiscount(currentDiscount, targetCouponId, fetchedCoupons = {}) {
    if (!currentDiscount || !targetCouponId) {
        return null;
    }

    // We should always have the actual coupon data if this function is called
    if (fetchedCoupons[targetCouponId]) {
        return {
            ...currentDiscount,
            coupon: fetchedCoupons[targetCouponId],
        };
    }

    // If we don't have the coupon data, return null to avoid showing incorrect discount
    return null;
}

/**
 * Calculates the effective end date for a coupon discount
 * @typedef {object} DiscountData
 * @property {import('stripe').Stripe.Coupon} coupon - The coupon object
 * @property {number} [start] - Unix timestamp for when the discount starts
 * @property {number} [end] - Unix timestamp for when the discount ends
 *
 * @param {DiscountData} discount - The discount object containing coupon and dates
 * @returns {Date|null} - The effective end date or null if not applicable
 */
export function getEffectiveCouponEndDate(discount) {
    if (!discount || !discount.coupon) {
        return null;
    }

    let effectiveEndDate = null;

    // Calculate the effective end date for repeating coupons with duration_in_months
    if (discount.coupon.duration === 'repeating' && discount.coupon.duration_in_months) {
        // For coupons with duration_in_months, calculate based on start + duration_in_months
        const startDate = discount?.start ? new Date(fromUnixTime(discount.start)) : new Date();
        const calculatedEndDate = addMonths(startDate, discount.coupon.duration_in_months);

        // If there's also an explicit end date, use whichever comes first
        if (discount?.end) {
            const explicitEndDate = new Date(fromUnixTime(discount.end));
            effectiveEndDate = isBefore(explicitEndDate, calculatedEndDate)
                ? explicitEndDate
                : calculatedEndDate;
        } else {
            effectiveEndDate = calculatedEndDate;
        }
    } else if (discount?.end) {
        // For other cases, just use the explicit end date if provided
        effectiveEndDate = new Date(fromUnixTime(discount.end));
    }

    return effectiveEndDate;
}

/**
 * Private helper function to update and verify coupon application
 * with retries and exponential backoff.
 * @param {{customerId: string, targetCouponId: string, errorMessage: string}} params
 * @returns {Promise<StripeDiscount>} Verified discount object
 * @private
 */
async function _updateAndVerifySignupCoupon({ customerId, targetCouponId, errorMessage }) {
    const result = await updateCustomerDiscount({
        customerId,
        couponId: targetCouponId,
    });

    if (!result.discount) {
        throw new Error(errorMessage);
    }

    // Verify the coupon was successfully applied
    const maxRetries = 3;
    let retryCount = 0;
    let verifiedDiscount = null;

    while (retryCount < maxRetries) {
        // Exponential backoff: 2s, 3s, 4.5s
        await sleep(INITIAL_RETRY_DELAY_MS * Math.pow(BACKOFF_MULTIPLIER, retryCount));

        const verificationResult = await getDiscountDetails({ customerId });

        if (verificationResult?.discount?.coupon?.id === targetCouponId) {
            verifiedDiscount = verificationResult.discount;
            break;
        }

        retryCount = retryCount + 1;
    }

    if (!verifiedDiscount) {
        throw new Error(`${errorMessage} after multiple attempts`);
    }

    return verifiedDiscount;
}
